import React from 'react';
import {
    Avatar,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Typography,
    Button,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import hajaoDeveloper from '../assets/images/hajaoDeveloper.png';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import WorkIcon from '@mui/icons-material/Work';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import { Link } from 'react-router-dom';

/* 🌟 Animations */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* 🌟 Styled Components */

// Main container with background and size constraints
const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: '1000px',
    margin: '180px auto',
    backgroundImage:
        'linear-gradient(rgba(16,7,32,0.8), rgba(16,7,32,0.8)), url("https://source.unsplash.com/1600x900/?technology,abstract")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: '#f0f0f0',
    fontFamily: "'Roboto', sans-serif",
    borderRadius: '12px',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        minWidth: '100%',
    },
}));

// Left image section
const ImageContainer = styled('div')(({ theme }) => ({
    flex: 1,
    textAlign: 'center',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
}));

// Profile image with gradient overlay
const ProfileImageWrapper = styled('div')({
    position: 'relative',
    width: '100%',
    maxWidth: '350px',
    borderRadius: '5px',
    overflow: 'hidden',
    boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
});

const ProfileImage = styled('img')({
    width: '100%',
    height: 'auto',
    display: 'block',
});

const GradientOverlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
        'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.1))',
});

// Right info section with fade-in
const InfoContainer = styled('div')(({ theme }) => ({
    flex: 2,
    padding: theme.spacing(5),
    backgroundColor: 'rgba(255,255,255,0.95)',
    color: '#100720',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    animation: `${fadeIn} 1s ease-out`,
}));

// Typography styles
const Title = styled(Typography)(({ theme }) => ({
    fontSize: '1.5rem',
    fontWeight: '700',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: '500',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: '#100720',
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
    fontSize: '1.2rem',            // reduced from 1.5rem
    fontWeight: '700',
    marginTop: theme.spacing(1),   // 25px top margin
    marginBottom: theme.spacing(1),// 25px bottom margin
    color: theme.palette.primary.main,
}));

const Description = styled(Typography)(({ theme }) => ({
    fontSize: '0.95rem',           // slightly smaller
    marginTop: theme.spacing(1),   // 25px top margin
    marginBottom: theme.spacing(1),// 25px bottom margin
    lineHeight: 1.6,
    color: '#100720',
}));


// Contact list items
const ListContainer = styled(ListItem)(({ theme }) => ({
    background:" linear-gradient(90deg, #f7e600, #daa520)",
    borderRadius: '8px',
    marginBottom: theme.spacing(1),
    color: '#f0f0f0',
}));

// Link button
const LinkButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(0),
    alignSelf: 'flex-start',
    background:" linear-gradient(90deg, #f7e600, #daa520)",
    color: '#fff',
    fontWeight: '600',
    textTransform: 'none',
    '&:hover': { backgroundColor: theme.palette.primary.dark },
}));

/* 🌟 Main Component */
function AuthorInfo() {
    return (
        <Container elevation={1}>
            {/* Left Section */}
            <ImageContainer>
                <ProfileImageWrapper>
                    <ProfileImage src={hajaoDeveloper} alt="Ajao Hazzan"/>
                    <GradientOverlay/>
                </ProfileImageWrapper>

                <ListContainer>
                    <ListItemAvatar><Avatar><EmailIcon/></Avatar></ListItemAvatar>
                    <ListItemText primary="hazzanaj@gmail.com"/>
                </ListContainer>
                <ListContainer>
                    <ListItemAvatar><Avatar><WorkIcon/></Avatar></ListItemAvatar>
                    <ListItemText
                        primary="Fullstack Software Developer | Embedded System Engineer | Freelance Consultant"/>
                </ListContainer>
                <ListContainer>
                    <ListItemAvatar><Avatar><PlaceIcon/></Avatar></ListItemAvatar>
                    <ListItemText primary="Finland"/>
                </ListContainer>
            </ImageContainer>

            {/* Right Section */}
            <InfoContainer>
                <Title>AJAO HAZZAN</Title>
                  ND Electrical Electronics, BEng(Hons) Electronics, MEng (Embedded Systems Engineering)
                <SectionHeader>Professions</SectionHeader>
                <Subtitle>Fullstack Software Engineer</Subtitle>
                <Description>
                    <FormatQuoteIcon fontSize="small"/> <br/>
                    I am a Java Fullstack Web Developer. I design and develop web applications from
                    Backend to Frontend depending on project demands.
                </Description>
                <LinkButton component={Link} to="/javascriptprojects" startIcon={<AddToHomeScreenIcon/>}>
                    View my project list
                </LinkButton>

                <SectionHeader>Research Interest</SectionHeader>
                <Description>
                    <FormatQuoteIcon fontSize="small"/> <br/>
                    As an Embedded Systems Engineer, I design embedded hardware and analogue circuits
                    using EDA tools like KiCad, Eagle, Proteus, and Altium. My extensive knowledge of
                    Java and C++ programming gives me in-depth expertise in microcontroller programming.
                </Description>
                <LinkButton component={Link} to="/javascriptprojects" startIcon={<AddToHomeScreenIcon/>}>
                    Explore my research projects
                </LinkButton>
            </InfoContainer>
        </Container>
    );
}

export default AuthorInfo;
